import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import {
  SIDEBAR_SORT_SECTIONS,
  getSidebarSortOptions,
  resolveSidebarSort,
  sortSidebarItems,
} from 'dashboard/helper/sidebarSort';

import { buildSidebarMenuItems } from './navigation';
import { TICKET_COUNT_GETTERS } from './navigation/tickets';
import { getSidebarAreaForRoute } from 'dashboard/components-next/rail/sections';
import { campaignChannelCounts } from 'dashboard/components-next/Campaigns/helpers/campaignFilters';

// On mobile a rail tap previews a section's menu in the drawer without
// navigating, so the active area can't come from the route alone. Module scope
// for the same reason as `setPanelLinkCount` (see useSidebarMode).
const mobilePreviewArea = ref(null);

export const setMobilePreviewArea = area => {
  mobilePreviewArea.value = area;
};

/**
 * Owns everything the sidebar navigation needs: the store data it reads, the
 * feature gates it honours, sort preferences, and the assembled `menuItems`.
 *
 * The item definitions themselves live in `./navigation/*` - one module per
 * top-level section - so that concurrent work on different sections does not
 * collide in a single file.
 */
export function useSidebarNavigation() {
  const store = useStore();
  const route = useRoute();
  const router = useRouter();
  const { t } = useI18n();
  const { accountScopedRoute, currentAccount } = useAccount();
  const { shouldShowPaywall, checkPermissions } = usePolicy();

  const accountId = useMapGetter('getCurrentAccountId');
  const currentUserId = useMapGetter('getCurrentUserID');
  const isFeatureEnabledonAccount = useMapGetter(
    'accounts/isFeatureEnabledonAccount'
  );

  const hasAdvancedAssignment = computed(() =>
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.ADVANCED_ASSIGNMENT
    )
  );

  const hasCaptainEnabled = computed(() =>
    isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.CAPTAIN)
  );

  const hasCaptainV2 = computed(() =>
    isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.CAPTAIN_V2)
  );

  const captainAssistants = useMapGetter('captainAssistants/getRecords');

  // The "More" fold at the end of the Conversations group: collapsed by
  // default, persisted per user like the other sidebar UI state.
  const { uiSettings, updateUISettings } = useUISettings();
  const showConversationsMore = computed(() =>
    Boolean(uiSettings.value.sidebar_conversations_more_open)
  );
  const toggleConversationsMore = () => {
    updateUISettings({
      sidebar_conversations_more_open: !showConversationsMore.value,
    });
  };

  // Same fold for the per-state ticket views at the end of the Tickets group.
  const showTicketsMore = computed(() =>
    Boolean(uiSettings.value.sidebar_tickets_more_open)
  );
  const toggleTicketsMore = () => {
    updateUISettings({
      sidebar_tickets_more_open: !showTicketsMore.value,
    });
  };

  const hasConversationUnreadCounts = computed(() =>
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONVERSATION_UNREAD_COUNTS
    )
  );

  const hasFilteredUnreadCounts = computed(
    () =>
      hasConversationUnreadCounts.value &&
      isFeatureEnabledonAccount.value(
        accountId.value,
        FEATURE_FLAGS.UNREAD_COUNT_FOR_FILTERS
      )
  );

  const hasTickets = computed(() =>
    isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.TICKETS)
  );

  // The ticket engine's own queue. Same flag the routes and the API carry, so
  // the panel never offers a link onto a surface the account has no engine for.
  const hasSupportCases = computed(() =>
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.SUPPORT_CASES
    )
  );

  const hasDataImport = computed(() =>
    isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.DATA_IMPORT)
  );

  // Outbound MCP. Same flag the connectors controller checks, so the panel
  // never offers a link onto a surface the API would 403.
  const hasMcpClient = computed(() =>
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.TOLA_AI_MCP_CLIENT
    )
  );

  // SAML is the entire Security page: when the plan paywalls it there is
  // nothing behind the link but an upgrade prompt, so drop the entry rather
  // than lead people to a dead end. The route itself stays reachable by URL.
  const hasSecuritySettings = computed(
    () => !shouldShowPaywall(FEATURE_FLAGS.SAML)
  );

  const inboxes = useMapGetter('inboxes/getInboxes');
  const labels = useMapGetter('labels/getLabelsOnSidebar');
  const teams = useMapGetter('teams/getMyTeams');
  const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
  const conversationCustomViews = useMapGetter(
    'customViews/getConversationCustomViews'
  );
  const ticketCustomViews = useMapGetter('customViews/getTicketViews');

  // One CustomFilter management pair for every sidebar view leaf -
  // conversation folders, ticket views and contact segments all go through
  // the same store module and the same ⋯ menu (ViewLeaf). When the deleted
  // view is the one on screen, its route would point at a record that no
  // longer exists, so each surface names the list to fall back to.
  const deleteView = async (view, { filterType, isActive, fallbackRoute }) => {
    await store.dispatch('customViews/delete', { id: view.id, filterType });
    if (isActive) router.push(fallbackRoute);
  };

  const toggleViewShared = (view, filterType) => {
    store.dispatch('customViews/update', {
      ...view,
      filter_type: filterType,
      shared: !view.shared,
    });
  };

  const deleteTicketView = view =>
    deleteView(view, {
      filterType: 'conversation',
      isActive: route.query.custom_view === String(view.id),
      fallbackRoute: accountScopedRoute('tickets_index'),
    });

  const deleteConversationView = view =>
    deleteView(view, {
      filterType: 'conversation',
      isActive:
        ['folder_conversations', 'conversations_through_folders'].includes(
          route.name
        ) && Number(route.params.id) === view.id,
      fallbackRoute: accountScopedRoute('home'),
    });

  const deleteContactView = view =>
    deleteView(view, {
      filterType: 'contact',
      isActive:
        ['contacts_dashboard_segments_index', 'contacts_edit_segment'].includes(
          route.name
        ) && Number(route.params.segmentId) === view.id,
      fallbackRoute: accountScopedRoute(
        'contacts_dashboard_index',
        {},
        { page: 1 }
      ),
    });

  const allUnreadCount = useMapGetter(
    'conversationUnreadCounts/getAllUnreadCount'
  );
  const getInboxUnreadCount = useMapGetter(
    'conversationUnreadCounts/getInboxUnreadCount'
  );
  const getLabelUnreadCount = useMapGetter(
    'conversationUnreadCounts/getLabelUnreadCount'
  );
  const getTeamUnreadCount = useMapGetter(
    'conversationUnreadCounts/getTeamUnreadCount'
  );
  const mentionsUnreadCount = useMapGetter(
    'conversationUnreadCounts/getMentionsUnreadCount'
  );
  const participatingUnreadCount = useMapGetter(
    'conversationUnreadCounts/getParticipatingUnreadCount'
  );
  const unattendedUnreadCount = useMapGetter(
    'conversationUnreadCounts/getUnattendedUnreadCount'
  );
  const getFolderUnreadCount = useMapGetter(
    'conversationUnreadCounts/getFolderUnreadCount'
  );
  const aiHandlingCount = useMapGetter(
    'conversationUnreadCounts/getAiHandlingCount'
  );
  // Total tickets per view, not unread - see TICKET_COUNT_GETTERS.
  const getTicketViewCount = useMapGetter(TICKET_COUNT_GETTERS.perView);
  const getSidebarSectionSort = useMapGetter(
    'sidebarSortPreferences/getSectionSort'
  );

  const fetchConversationUnreadCounts = ([currentAccountId, isEnabled]) => {
    if (!currentAccountId) return;

    if (!isEnabled) {
      store.dispatch('conversationUnreadCounts/clear');
      return;
    }

    store.dispatch('conversationUnreadCounts/get');
  };

  const fetchSidebarSortPreferences = ([currentAccountId, userId]) => {
    if (!currentAccountId || !userId) return;
    store.dispatch('sidebarSortPreferences/initialize');
  };

  onMounted(() => {
    store.dispatch('labels/get');
    store.dispatch('inboxes/get');
    store.dispatch('notifications/unReadCount');
    store.dispatch('teams/get');
    store.dispatch('attributes/get');
    store.dispatch('customViews/get', 'conversation');
    store.dispatch('customViews/get', 'contact');
  });

  watch(
    [accountId, hasConversationUnreadCounts],
    fetchConversationUnreadCounts,
    { immediate: true }
  );

  watch([accountId, currentUserId], fetchSidebarSortPreferences, {
    immediate: true,
  });

  const hasUnreadCountsForSection = section => {
    if (section === SIDEBAR_SORT_SECTIONS.FOLDERS) {
      return hasFilteredUnreadCounts.value;
    }

    return hasConversationUnreadCounts.value;
  };

  const getSortOptionsForSection = section =>
    getSidebarSortOptions(section, {
      hasUnreadCounts: hasUnreadCountsForSection(section),
    });

  const getSortForSection = section =>
    resolveSidebarSort(section, getSidebarSectionSort.value(section), {
      hasUnreadCounts: hasUnreadCountsForSection(section),
    });

  const updateSortPreference = (section, sortBy) => {
    store.dispatch('sidebarSortPreferences/setSectionSort', {
      section,
      sortBy,
    });
  };

  const buildSortConfig = section => ({
    sortOptions: getSortOptionsForSection(section),
    activeSort: getSortForSection(section),
    onSortChange: sortBy => updateSortPreference(section, sortBy),
  });

  const sortedFolders = computed(() =>
    sortSidebarItems(conversationCustomViews.value, {
      sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.FOLDERS),
      labelKey: view => view.name,
      unreadCountKey: view => getFolderUnreadCount.value(view.id),
    })
  );

  const sortedTeams = computed(() =>
    sortSidebarItems(teams.value, {
      sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.TEAMS),
      labelKey: team => team.name,
      unreadCountKey: team => getTeamUnreadCount.value(team.id),
    })
  );

  const sortedInboxes = computed(() =>
    sortSidebarItems(inboxes.value, {
      sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.CHANNELS),
      labelKey: inbox => inbox.name,
      unreadCountKey: inbox => getInboxUnreadCount.value(inbox.id),
    })
  );

  const sortedLabels = computed(() =>
    sortSidebarItems(labels.value, {
      sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.LABELS),
      labelKey: label => label.title,
      unreadCountKey: label => getLabelUnreadCount.value(label.id),
    })
  );

  const activeProductArea = computed(
    () => mobilePreviewArea.value ?? getSidebarAreaForRoute(route.name)
  );

  const campaigns = useMapGetter('campaigns/getAllCampaigns');
  const campaignCounts = computed(() => campaignChannelCounts(campaigns.value));

  // The Campaigns entries carry a count, so the list has to be loaded before
  // the panel that shows them - but only there, and only for the admins the
  // campaigns API answers to. Campaign pages fetch it again on their own.
  watch(
    [activeProductArea, accountId],
    ([productArea, currentAccountId]) => {
      if (productArea !== 'customers' || !currentAccountId) return;
      if (!checkPermissions(['administrator'])) return;
      store.dispatch('campaigns/get');
    },
    { immediate: true }
  );

  // The AI panel lists the assistants themselves, so the list has to be loaded
  // before the panel that shows them. The Captain pages fetch it again on their
  // own.
  watch(
    [activeProductArea, accountId, hasCaptainEnabled],
    ([productArea, currentAccountId, captainEnabled]) => {
      if (productArea !== 'ai' || !currentAccountId || !captainEnabled) return;
      store.dispatch('captainAssistants/get');
    },
    { immediate: true }
  );

  const menuItems = computed(() =>
    buildSidebarMenuItems(
      {
        t,
        accountScopedRoute,
        captainAssistants: captainAssistants.value,
        hasCaptainV2: hasCaptainV2.value,
        buildSortConfig,
        hasAdvancedAssignment: hasAdvancedAssignment.value,
        hasCaptainEnabled: hasCaptainEnabled.value,
        hasTickets: hasTickets.value,
        hasSupportCases: hasSupportCases.value,
        ticketCustomViews: ticketCustomViews.value,
        currentUserId: currentUserId.value,
        isAdmin: checkPermissions(['administrator']),
        onDeleteTicketView: deleteTicketView,
        onToggleTicketViewShared: view =>
          toggleViewShared(view, 'conversation'),
        onDeleteConversationView: deleteConversationView,
        onToggleConversationViewShared: view =>
          toggleViewShared(view, 'conversation'),
        onDeleteContactView: deleteContactView,
        onToggleContactViewShared: view => toggleViewShared(view, 'contact'),
        getTicketViewCount: getTicketViewCount.value,
        showConversationsMore: showConversationsMore.value,
        toggleConversationsMore,
        showTicketsMore: showTicketsMore.value,
        toggleTicketsMore,
        campaignCounts: campaignCounts.value,
        hasDataImport: hasDataImport.value,
        hasMcpClient: hasMcpClient.value,
        hasSecuritySettings: hasSecuritySettings.value,
        // Per-channel availability gates for the Settings Channels group.
        accountFeatures: currentAccount.value?.features || {},
        hasFilteredUnreadCounts: hasFilteredUnreadCounts.value,
        allUnreadCount: allUnreadCount.value,
        mentionsUnreadCount: mentionsUnreadCount.value,
        participatingUnreadCount: participatingUnreadCount.value,
        unattendedUnreadCount: unattendedUnreadCount.value,
        aiHandlingCount: aiHandlingCount.value,
        labels: labels.value,
        contactCustomViews: contactCustomViews.value,
        sortedFolders: sortedFolders.value,
        sortedTeams: sortedTeams.value,
        sortedInboxes: sortedInboxes.value,
        sortedLabels: sortedLabels.value,
        getFolderUnreadCount: getFolderUnreadCount.value,
        getTeamUnreadCount: getTeamUnreadCount.value,
        getInboxUnreadCount: getInboxUnreadCount.value,
        getLabelUnreadCount: getLabelUnreadCount.value,
      },
      activeProductArea.value
    )
  );

  // A panel is only worth showing when it offers a choice. Counting the
  // navigable links generically means any future single-link section gets the
  // rail-only treatment without anyone remembering to flag it.
  const countNavigableLinks = items =>
    items.reduce((total, item) => {
      if (Array.isArray(item.children) && item.children.length) {
        return total + countNavigableLinks(item.children);
      }
      return total + (item.to ? 1 : 0);
    }, 0);

  const panelLinkCount = computed(() => countNavigableLinks(menuItems.value));

  return { menuItems, panelLinkCount, activeProductArea };
}
